import React, { useEffect, useState } from 'react';

import { useQuizContext } from '../../context/QuizContext';
import { FormattedMessage } from 'react-intl';

import { Box, Flex } from '@osmosis/react-ui/components/Box';
import { H1, H2, P } from '@osmosis/react-ui/components/Text';
import { Button } from '@osmosis/react-ui/components/Button';
import Image from 'next/image';
import moCelebrating from '../../assets/mo-shimmy.webp';

import { QuizTypes } from '../../types/quiz';
import SemiCircleProgressBar from '../SemiCircleProgressBar';
import QuizXPCount from './QuizXpCount';
import QuizConfidenceSummary from './QuizConfidenceSummary';

/**
 * QuizSummary component displays the summary of the quiz results including correct and incorrect answers,
 * confidence levels, and options to repeat missed questions or start a new quiz.
 */
const QuizSummary = () => {
  const {
    correctItems,
    incorrectItems,
    totalItems,
    startQuiz,
    isDailyPractice,
    quizType,
    xp,
    getGroupQuizResponses,
  } = useQuizContext();

  const [dailyStreak, setDailyStreak] = useState(0);
  useEffect(() => {
    if (isDailyPractice) {
      const fetchStreak = async () => {
        try {
          const response = await fetch('/data/user/streak');
          const data = await response.json();
          setDailyStreak(data.streak);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching streak:', error);
        }
      };
      fetchStreak();
    }
  }, [isDailyPractice]);

  const repeatMissed = () => {
    const newItems = incorrectItems().map((i) => ({
      item: i.item,
    }));
    startQuiz(newItems);
  };

  const correctCount = correctItems().length;
  const incorrectCount = incorrectItems().length;
  const scorePercent =
    totalItems === 0
      ? 0
      : (((correctCount / totalItems) * 100).toFixed(0) as unknown as number);

  const { correctLow, correctMedium, correctHigh } =
    getGroupQuizResponses(correctItems());
  const { incorrectLow, incorrectMedium, incorrectHigh } =
    getGroupQuizResponses(incorrectItems());

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      py={4}
      width={670}
      mx="auto"
      color="#404040"
      data-testid="quiz-summary-container"
    >
      <H1
        textAlign="center"
        mb={3}
        color="black"
        data-testid="quiz-summary-title"
      >
        {isDailyPractice ? (
          <FormattedMessage
            defaultMessage="Congrats! You have completed the daily quiz."
            id="quiz.quizSummary.congrats"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Congrats! You did it!"
            id="quiz.quizSummary.heading"
          />
        )}
      </H1>
      <Image
        height={132}
        src={moCelebrating}
        alt="celebrating Mo"
        data-testid="quiz-summary-image"
      />
      {!isDailyPractice && (
        <>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '32px',
              marginTop: '20px',
            }}
            data-testid="quiz-summary-buttons"
          >
            {incorrectItems().length !== 0 && (
              <Button
                fontSize={16}
                backgroundColor="white"
                color="text"
                variant="outline"
                onClick={repeatMissed}
                paddingX="24px"
                paddingY="8px"
                height="40px"
                data-testid="quiz.quizSummary.button.repeatMissed"
              >
                <FormattedMessage
                  defaultMessage="Repeat incorrect questions"
                  id="quiz.quizSummary.button.repeatMissed"
                />
              </Button>
            )}

            <Button
              as="a"
              fontSize={16}
              backgroundColor="blue"
              paddingX="24px"
              paddingY="8px"
              height="40px"
              href={
                quizType === QuizTypes.Flashcards ? '/flashcards' : '/qbank'
              }
              data-testid="quiz.quizSummary.button.newQuiz"
            >
              <FormattedMessage
                defaultMessage="Create new quiz"
                id="quiz.quizSummary.button.newQuiz"
              />
            </Button>
          </Box>
        </>
      )}
      {/* Your score section */}
      <Box width="100%" my={4} data-testid="quiz-summary-score-section">
        <H2
          sx={{ fontSize: '18px', color: '#404040' }}
          data-testid="quiz-summary-score-title"
        >
          <FormattedMessage
            defaultMessage="Your score"
            id="quiz.summary.scoreLabel"
          />
        </H2>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <SemiCircleProgressBar
            correct={correctCount}
            incorrect={incorrectCount}
            total={totalItems}
          />
          <Box
            style={{
              position: 'absolute',
              left: '33%',
              top: '60%',
              transform: 'translate(-50%, -50%)',
              width: 'max-content',
              textAlign: 'center',
              color: '#404040',
            }}
            data-testid="quiz-summary-score-value"
          >
            <H2 color="#404040" data-testid="score-percent">
              <FormattedMessage
                defaultMessage="{scorePercent}%"
                id="quiz.summary.scorePercent"
                values={{ scorePercent }}
              />
            </H2>
            <H2 fontSize={16} data-testid="score-correct-section">
              <FormattedMessage
                defaultMessage="correct"
                id="quiz.summary.scoreDetails"
              />
            </H2>
          </Box>
          <Box
            justifyContent="center"
            mt={4}
            marginLeft={4}
            data-testid="quiz-summary-breakdown"
          >
            <Box
              alignItems="center"
              sx={{ display: 'flex', gap: 2, marginTop: '24px' }}
            >
              <Box className="inline-block w-4 h-4 rounded" bg="#16A34A" />
              <P fontSize={14} data-testid="quiz-correct-count">
                <FormattedMessage
                  defaultMessage="{scorePercent}% correct ({correctCount} questions)"
                  id="quiz.summary.correct.count"
                  values={{ scorePercent, correctCount }}
                />
              </P>
            </Box>
            <Box
              alignItems="center"
              sx={{ display: 'flex', gap: 2, marginTop: '24px' }}
            >
              <Box className="inline-block w-4 h-4 rounded" bg="#B30000" />
              <P fontSize={14} data-testid="quiz-incorrect-count">
                <FormattedMessage
                  defaultMessage="{percent}% incorrect ({count} questions)"
                  id="quiz.summary.incorrect.count"
                  values={{
                    percent: 100 - scorePercent,
                    count: incorrectCount,
                  }}
                />
              </P>
            </Box>
          </Box>
        </Box>
      </Box>

      {isDailyPractice && (
        <>
          <Box width="100%" my={2} data-testid="quiz-summary-daily-rewards">
            <H2 sx={{ fontSize: '18px', color: '#404040' }}>
              <FormattedMessage
                defaultMessage="Your daily quiz rewards"
                id="quiz.quizSummary.dailyPracticeRewards"
              />
            </H2>
            <QuizXPCount streak={dailyStreak!} xp={xp.total} />
          </Box>
          <Box my={2}>
            <Button
              as="a"
              fontSize={16}
              fontWeight={800}
              backgroundColor="blue"
              sx={{ paddingY: '8px', paddingX: '24px', height: '40px' }}
              href="/home/dashboard"
              data-testid="quiz.quizSummary.button.backToDashboard"
            >
              <FormattedMessage
                defaultMessage="Back to dashboard"
                id="quiz.quizSummary.button.backToDashboard"
              />
            </Button>
          </Box>
        </>
      )}

      {!isDailyPractice && (
        <>
          {/* Confidence and accuracy breakdown */}

          <Box
            sx={{ marginY: '20px' }}
            data-testid="quiz-summary-breakdown-title"
          >
            <H2 sx={{ fontSize: '18px', color: '#404040' }}>
              <FormattedMessage
                defaultMessage="Confidence and accuracy breakdown"
                id="quiz.summary.breakdown.title"
              />
            </H2>

            <P fontSize={16} my={3} color="#404040">
              <FormattedMessage
                defaultMessage="Here’s your confidence breakdown: below, you'll find the number of questions you got right and wrong at each confidence level-low, medium, and high."
                id="quiz.summary.breakdown.description"
              />
            </P>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              gap: 5,
              marginBottom: '20px',
              width: '100%',
            }}
          >
            <Box width="50%" data-testid="quiz-summary-confidence-correct">
              <QuizConfidenceSummary
                color="#16A34A"
                confidence={{
                  low: correctLow,
                  medium: correctMedium,
                  high: correctHigh,
                }}
                sectionType="correct"
              />
            </Box>
            <Box width="50%">
              <QuizConfidenceSummary
                color="#B30000"
                confidence={{
                  low: incorrectLow,
                  medium: incorrectMedium,
                  high: incorrectHigh,
                }}
                sectionType="incorrect"
              />
            </Box>
          </Box>
        </>
      )}
    </Flex>
  );
};

export default QuizSummary;
